import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Card,
  Alert
} from '@mui/material';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import CustomerLayout from '../../components/customer/CustomerLayout';
import customerApiService from '../../services/customerApi';

const CustomerCheckoutPageV2: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const transactionId = searchParams.get('_ptxn') || 
                        (location.state as any)?.transactionId || 
                        (location.state as any)?.checkoutId;
  const checkoutUrl = (location.state as any)?.checkoutUrl;
  
  const [loading, setLoading] = useState(true);
  const [checkoutOpened, setCheckoutOpened] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initCheckout = async () => {
      if (!transactionId && !checkoutUrl) {
        setError('No checkout information provided');
        setLoading(false);
        return;
      }

      try {
        const config = await customerApiService.getBillingConfig();
        const clientToken = config.paddle_client_token;

        // Load Paddle.js
        const script = document.createElement('script');
        script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
        script.async = true;
        
        script.onload = () => {
          const w = window as any;
          if (w.Paddle) {
            w.Paddle.Environment.set('sandbox');
            w.Paddle.Initialize({ token: clientToken });
            
            // Open checkout in overlay mode
            if (transactionId) {
              w.Paddle.Checkout.open({
                transactionId: transactionId,
                settings: {
                  successUrl: `${window.location.origin}/customer/subscription?success=true`
                }
              });
              setCheckoutOpened(true);
            }
          }
          setLoading(false);
        };

        script.onerror = () => {
          setError('Failed to load Paddle checkout');
          setLoading(false);
        };

        document.body.appendChild(script);

        return () => {
          document.body.removeChild(script);
        };
      } catch (err: any) {
        setError('Failed to initialize checkout');
        setLoading(false);
      }
    };

    initCheckout();
  }, [transactionId, checkoutUrl]);

  if (loading) {
    return (
      <CustomerLayout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 6 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Loading secure checkout...
            </Typography>
          </Card>
        </Container>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 6 }}>
            <Box mb={2}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Checkout Ready
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {checkoutOpened 
                ? 'Your checkout window should open automatically. If not, click the button below.'
                : 'Click the button below to complete your purchase securely.'}
            </Typography>

            {checkoutUrl && (
              <Button
                variant="contained"
                size="large"
                href={checkoutUrl}
                target="_blank"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  mb: 2
                }}
              >
                Complete Purchase
              </Button>
            )}

            <Box mt={3}>
              <Button
                variant="text"
                onClick={() => navigate('/customer/subscription')}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Return to Subscription
              </Button>
            </Box>
          </Card>
        )}

        {/* Fallback actions */}
        {error && (
          <Box textAlign="center" mt={3}>
            <Button
              variant="outlined"
              onClick={() => navigate('/customer/plans')}
              sx={{
                px: 4,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2
              }}
            >
              View Plans
            </Button>
          </Box>
        )}
      </Container>
    </CustomerLayout>
  );
};

export default CustomerCheckoutPageV2;

